import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * VA-28992 / VA-28993: the cost-optimized conversation timings an admin can tune.
 *
 * - Thinking time: silence after the interviewer finishes before the gentle
 *   "take your time" reminder, then before the question is moved past.
 * - Final window: the last stretch of the interview in which no new question is
 *   asked; the answer in progress is finished, then the interview closes (0 = off).
 *
 * Saved on the same VAD settings document the backend reads at session start, so a
 * save applies to the next interviews. Bounds and defaults come from the backend.
 */
import { useEffect, useState } from 'react';
import { RotateCcw, Save } from 'lucide-react';
import { useToast } from '@/hoc';
import { Button } from '../ui/button';
import { useSaveVadSettings, useVadSettings } from './voice-admin-api';
/** Mirrors interview/cost_optimized/turn_timing_settings.py; the backend's values win. */
export const DEFAULT_TIMING = {
    no_response_reminder_sec: 25,
    no_response_auto_skip_sec: 20,
    final_window_sec: 30,
};
export const DEFAULT_TIMING_BOUNDS = {
    no_response_reminder_sec: [5, 120],
    no_response_auto_skip_sec: [5, 120],
    final_window_sec: [0, 300],
};
const FIELDS = [
    {
        field: 'no_response_reminder_sec',
        label: 'Thinking time before the reminder',
        hint: 'Silence after a question before the interviewer says "take your time".',
    },
    {
        field: 'no_response_auto_skip_sec',
        label: 'Further wait before moving on',
        hint: 'Silence after that reminder before the question is moved past.',
    },
    {
        field: 'final_window_sec',
        label: 'Final window (no new questions)',
        hint: 'In the last stretch the open answer is finished, then the interview closes. 0 turns it off.',
    },
];
export const timingFromSettings = (settings) => {
    const out = Object.assign({}, DEFAULT_TIMING);
    Object.keys(out).forEach((field) => {
        const value = settings === null || settings === void 0 ? void 0 : settings[field];
        if (typeof value === 'number' && Number.isFinite(value))
            out[field] = value;
    });
    return out;
};
const ConversationTimingPanel = () => {
    const { showToast } = useToast();
    const { data: settings } = useVadSettings();
    const save = useSaveVadSettings();
    const [form, setForm] = useState(Object.assign({}, DEFAULT_TIMING));
    const [dirty, setDirty] = useState(false);
    // Start from the saved values (until the admin starts editing).
    useEffect(() => {
        if (settings && !dirty)
            setForm(timingFromSettings(settings));
    }, [settings, dirty]);
    const bounds = (field) => { var _a, _b; return (_b = (_a = settings === null || settings === void 0 ? void 0 : settings.timing_bounds) === null || _a === void 0 ? void 0 : _a[field]) !== null && _b !== void 0 ? _b : DEFAULT_TIMING_BOUNDS[field]; };
    const onChange = (field, value) => {
        setDirty(true);
        setForm((prev) => (Object.assign(Object.assign({}, prev), { [field]: value })));
    };
    const onSave = () => {
        save.mutate(form, {
            onSuccess: (saved) => {
                setDirty(false);
                setForm(timingFromSettings(saved));
                showToast({
                    message: 'Conversation timing saved',
                    description: 'The next interviews use it.',
                    status: 'success',
                });
            },
            onError: (err) => showToast({
                message: 'Could not save conversation timing',
                description: err instanceof Error ? err.message : String(err),
                status: 'error',
            }),
        });
    };
    const onResetDefaults = () => {
        setDirty(true);
        setForm(Object.assign(Object.assign({}, DEFAULT_TIMING), settings === null || settings === void 0 ? void 0 : settings.timing_defaults));
    };
    return (_jsxs("div", { className: 'space-y-4', children: [_jsx("p", { className: 'text-sm text-dash-muted', children: "How long the interviewer waits for the candidate to think, and when it stops asking new questions near the end. Cost-optimized interviews only." }), _jsx("div", { className: 'grid grid-cols-1 gap-4 md:grid-cols-3', children: FIELDS.map(({ field, label, hint }) => {
                    const [min, max] = bounds(field);
                    return (_jsxs("label", { className: 'block text-xs font-medium text-dash-muted', children: [label, ": ", _jsxs("span", { className: 'text-dash-text', children: [form[field], " s"] }), _jsx("input", { type: 'range', min: min, max: max, step: field === 'final_window_sec' ? 5 : 1, value: form[field], onChange: (e) => onChange(field, Number(e.target.value)), className: 'mt-1 w-full', "aria-label": label }), _jsx("span", { className: 'mt-0.5 block text-[11px] font-normal text-dash-muted', children: hint })] }, field));
                }) }), _jsxs("div", { className: 'flex flex-wrap items-center justify-end gap-2 border-t border-dash-border pt-3', children: [_jsx(Button, { variant: 'outline', size: 'sm', onClick: onResetDefaults, rightIcon: _jsx(RotateCcw, { className: 'h-4 w-4' }), children: "Reset to defaults" }), _jsx(Button, { size: 'sm', onClick: onSave, disabled: !dirty, isLoading: save.isPending, rightIcon: _jsx(Save, { className: 'h-4 w-4' }), children: "Save as interview defaults" })] })] }));
};
export default ConversationTimingPanel;
